import { CustomerDeeplinks } from '@owner/deeplinks'

import { analytics } from '@/plugins/analytics'

declare global {
  interface Document {
    prerendering?: boolean
  }
}

// Deeplinks are externally openable, so `utm_source` is attacker-supplied:
// anything outside the set Olympus emits is dropped to keep the PostHog channel
// breakdown from accruing unbounded values.
const ATTRIBUTED_CHANNELS = ['email', 'sms', 'push'] as const

type AttributedChannel = (typeof ATTRIBUTED_CHANNELS)[number]

/**
 *
 * @param url
 */
function parseUtmSource(url: URL): AttributedChannel | null {
  const raw = url.searchParams.get('utm_source')
  return ATTRIBUTED_CHANNELS.find((channel) => channel === raw) ?? null
}

/**
 * Operator web-fallback first-party click capture. When an operator lands on a
 * dashboard page that is a `tracked` customer deeplink AND carries its stable-key
 * marker, capture a 'Deeplink Clicked' PostHog event. Registry-gated — dormant
 * until a customer destination opts in, so it needs no per-path logic.
 * Best-effort: never throws.
 * @param currentUrl
 */
export function recordDeeplinkClick(currentUrl: string): void {
  if (document.prerendering) {
    // A prerendered page hasn't been seen by the visitor yet; count the click on activation instead.
    document.addEventListener(
      'prerenderingchange',
      () => recordDeeplinkClick(currentUrl),
      { once: true }
    )
    return
  }

  let resolved
  let url
  try {
    url = new URL(currentUrl)
    resolved = new CustomerDeeplinks(url.origin).resolveTrackedClick(currentUrl)
  } catch {
    return
  }
  if (!resolved?.stableKey) return

  const brandId = (resolved.params as { brandId?: string }).brandId
  if (!brandId) return

  analytics.track('Deeplink Clicked', {
    brandId,
    deeplinkStableId: resolved.stableKey,
    linkKey: String(resolved.key),
    source: 'web',
    surface: 'operator',
    utm_source: parseUtmSource(url),
  })
}
